import React, { useEffect, useState } from 'react';
import { CalendarRange } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Tour } from '../../types/tours';

interface TourDetailGalleryProps {
  tour: Tour;
}

const TourDetailGallery: React.FC<TourDetailGalleryProps> = ({ tour }) => {
  const [nextAvailableDate, setNextAvailableDate] = useState<Date | null>(null);

  const carouselImages = tour.tour_images
    ?.filter(img => img.image_type === 'carousel')
    .sort((a, b) => a.display_order - b.display_order)
    .map(img => img.image_url) || [];

  const lowestPrice = Math.min(
    tour.group_tour_price,
    tour.reduced_group_price,
    tour.private_price
  );
  
  useEffect(() => {
    // Get next available date based on current time
    const getNextAvailableDate = () => {
      const [hours, minutes] = tour.departure.split(':').map(Number);
      const now = new Date();
      const tourTime = new Date();
      tourTime.setHours(hours, minutes, 0, 0);
      
      // If it's less than 6 hours until departure, it can be booked for today
      const hoursDiff = (tourTime.getTime() - now.getTime()) / (1000 * 60 * 60);
      
      if (hoursDiff >= 6) {
        setNextAvailableDate(tourTime);
      } else {
        // Set for tomorrow
        tourTime.setDate(tourTime.getDate() + 1);
        setNextAvailableDate(tourTime);
      }
    };

    getNextAvailableDate();
  }, [tour.departure]);

  return (
    <div className="container mx-auto px-4 max-h-[75vh]">
      <div className="relative h-[60vh] lg:h-[75vh] grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Main Image */}
        <div className="md:col-span-8 relative overflow-hidden rounded-2xl">
          {/* Blurred background */}
          <div className="absolute inset-0">
            <img
              src={carouselImages[0]}
              alt=""
              className="w-full h-full object-cover blur-md scale-110"
            />
          </div>
          {/* Main image with preserved aspect ratio */}
          <img
            src={carouselImages[0]}
            alt={tour.name}
            className="absolute inset-0 w-full h-full object-contain"
          />
          <div className="absolute bottom-4 right-4 flex justify-end">
            <Link
              to={`/tours/${tour.slug}/book?date=${nextAvailableDate?.toISOString()}`}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#953B0E] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors border-2 border-[#E85C0D]"
            >
              <span className="relative">
                <span className="absolute inset-0 rounded-full animate-ping bg-green-500/75"></span>
                <span className="relative block w-3 h-3 rounded-full bg-green-500"></span>
              </span>
              Book this tour for {
                nextAvailableDate?.getDate() === new Date().getDate() 
                  ? 'today'
                  : nextAvailableDate?.getDate() === new Date().getDate() + 1
                    ? 'tomorrow'
                    : nextAvailableDate?.toLocaleDateString('en-US', { weekday: 'long' })
              }
            </Link>
          </div>
        </div>

        {/* Side Images */}
        <div className="hidden md:col-span-4 md:grid grid-rows-2 gap-4">
          {carouselImages.slice(1, 3).map((image, index) => (
            <div key={index} className="relative overflow-hidden rounded-2xl bg-black">
              {/* Blurred background */}
              <div className="absolute inset-0">
                <img
                  src={image}
                  alt=""
                  className="w-full h-full object-cover blur-md scale-110 opacity-90"
                />
              </div>
              {/* Main image */}
              <img
                src={image}
                alt={`${tour.name} ${index + 2}`}
                className="absolute inset-0 w-full h-full object-contain"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TourDetailGallery;